import { ConnectionListResult, FeatureFlagsContext, GridSortProps, MaxCountsResultData, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamVirtualConnectionsFilteredByTableQuery,
  DatabaseDownstreamVirtualConnectionsFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamVirtualConnectionsFilteredByTableQuery';
import {
  DatabaseDownstreamVirtualConnectionsQuery,
  DatabaseDownstreamVirtualConnectionsQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamVirtualConnectionsQuery';
import {
  VirtualConnectionsDisplayGridRowDataFragment,
} from '../../../gen-src/queries/VirtualConnectionsDisplayGridRowDataFragment';
import { VirtualConnectionSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  DatabaseDownstreamVirtualConnectionsFilteredByTableQueryGql,
} from '../../queries/DatabaseDownstreamVirtualConnectionsFilteredByTableQueryGql';
import {
  DatabaseDownstreamVirtualConnectionsQueryGql,
} from '../../queries/DatabaseDownstreamVirtualConnectionsQueryGql';
import { getRowDataWithMaxCountsForVirtualConnection } from '../database/functions/VirtualConnectionRowDataFunctions';
import { VirtualConnectionsDisplayGridRowData } from '../database/types/VirtualConnectionDisplayGridRowData';
import { VirtualConnectionsDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionsDisplayGrid';
import { VirtualConnectionsValidSortFields } from '../sorting/sortProviders/VirtualConnectionsGridSortProvider';

type QueryVariables = DatabaseDownstreamVirtualConnectionsQueryVariables;
type QueryResults = DatabaseDownstreamVirtualConnectionsQuery;
type QueryResultListNode = VirtualConnectionsDisplayGridRowDataFragment;
type GridRowData = VirtualConnectionsDisplayGridRowData;

type QueryVariablesWithFilter = DatabaseDownstreamVirtualConnectionsFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamVirtualConnectionsFilteredByTableQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamVirtualConnectionsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamVirtualConnectionsConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.databases && resultData.databases[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.tables && resultData.tables[0];
}


function getRowDataForVirtualConnection(
  node: QueryResultListNode,
  resultData: QueryResults,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(node, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionForFilteredQuery(
  node: QueryResultListNode,
  resultData: QueryResultsWithFilter,
): VirtualConnectionsDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnection(node, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatabaseDownstreamVirtualConnectionsQueryGridProps =
  GridSortProps<VirtualConnectionsValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamVirtualConnectionsQueryGrid(props: DatabaseDownstreamVirtualConnectionsQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamVirtualConnectionsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredVirtualConnectionsLineageGridMessage();

  return props.filteredTableId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DatabaseDownstreamVirtualConnectionsFilteredByTableQueryGql}
      partialQueryVariables={{
        tableId: props.filteredTableId,
        orderBy: props.orderBy as VirtualConnectionSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionForFilteredQuery}
    >
      {({ rowStore }) => <VirtualConnectionsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={DatabaseDownstreamVirtualConnectionsQueryGql}
      partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as VirtualConnectionSortOrder, publishedConnectionsLineageEnabled }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnection}
    >
      {({ rowStore }) => <VirtualConnectionsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  );
}
